import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios";
import { useLang } from "../i18n/LanguageContext";
import TopBar from "../components/TopBar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LandingPage() {
  const { t, lang } = useLang();
  const es = lang === "es";

  const programs = [
    { icon: "🎯", key: "player_dev", title_es: "Player Development", title_en: "Player Development", desc_es: "Desarrollo integral · técnica, táctica, mentalidad", desc_en: "Full development · technique, tactics, mindset" },
    { icon: "⚽", key: "shooting", title_es: "Shooting & Finishing", title_en: "Shooting & Finishing", desc_es: "Definición y acabado en el arco", desc_en: "Finishing training and shot precision" },
    { icon: "👥", key: "small_group", title_es: "Small Group Training", title_en: "Small Group Training", desc_es: "Grupos de 3-6 jugadores · $45 c/u", desc_en: "3-6 player groups · $45 each" },
    { icon: "🏕️", key: "camps", title_es: "Camps", title_en: "Camps", desc_es: "Programas intensivos de temporada", desc_en: "Seasonal intensive programs" },
    { icon: "⭐", key: "elite", title_es: "Elite / Advanced", title_en: "Elite / Advanced", desc_es: "Desarrollo avanzado · jugadores competitivos", desc_en: "Advanced development · competitive players" },
  ];

  return (
    <div className="min-h-screen bg-[#080808] text-white overflow-hidden">
      <TopBar />
      <div
        className="absolute inset-0 pointer-events-none -z-0"
        style={{
          background:
            "radial-gradient(circle at 85% 10%, rgba(232,85,154,0.18) 0%, transparent 55%), radial-gradient(circle at 10% 90%, rgba(201,64,127,0.10) 0%, transparent 55%)",
        }}
      />

      {/* Hero */}
      <section className="relative max-w-6xl mx-auto px-4 sm:px-6 pt-12 pb-16 sm:pt-20 sm:pb-24">
        <div className="inline-flex items-center gap-2 rounded-full border border-[#e8559a]/30 bg-[#e8559a]/5 px-3 py-1 text-[10px] uppercase tracking-[0.3em] text-[#e8559a] mb-5">
          <span>●</span> Denver, Colorado · Est. 2022
        </div>
        <h1 className="font-bebas text-5xl sm:text-7xl lg:text-8xl tracking-[0.03em] leading-[0.95] max-w-4xl">
          {es ? "DESARROLLAMOS" : "WE DEVELOP"}
          <br />
          <span className="text-[#e8559a]">{es ? "JUGADORES," : "PLAYERS,"}</span>
          <br />
          {es ? "NO SOLO EQUIPOS." : "NOT JUST TEAMS."}
        </h1>
        <p className="mt-6 text-neutral-300 text-lg max-w-2xl leading-relaxed">
          {es
            ? "Soccer Troop es un proyecto de formación futbolística. Nuestro objetivo no es ganar campeonatos, es ayudar a cada jugador a mejorar su técnica, inteligencia de juego, confianza y comprensión del fútbol."
            : "Soccer Troop is a player development project. Our goal is not to win championships — it is to help every player improve their technique, game intelligence, confidence and understanding of the game."}
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <Link to="/programs" data-testid="cta-programs" className="px-6 py-3.5 rounded-xl bg-[#e8559a] hover:bg-[#c9407f] text-white font-bebas text-lg tracking-[0.2em] transition-colors shadow-lg shadow-[#e8559a]/30">
            {es ? "VER PROGRAMAS" : "VIEW PROGRAMS"} →
          </Link>
          <Link to="/register" data-testid="cta-register" className="px-6 py-3.5 rounded-xl bg-[#161616] border border-[#e8559a]/40 hover:border-[#e8559a] text-white font-bebas text-lg tracking-[0.2em] transition-colors">
            {es ? "REGISTRAR JUGADOR" : "PLAYER REGISTRATION"}
          </Link>
          <Link to="/portal" data-testid="cta-portal" className="px-6 py-3.5 rounded-xl bg-[#161616] border border-[#252525] hover:border-[#e8559a]/50 text-neutral-200 font-bebas text-lg tracking-[0.2em] transition-colors">
            {es ? "PORTAL DE PADRES" : "PARENT PORTAL"}
          </Link>
        </div>
      </section>

      {/* Philosophy */}
      <section className="relative max-w-6xl mx-auto px-4 sm:px-6 py-12">
        <div className="rounded-3xl bg-[#161616] border border-[#252525] p-8 sm:p-12">
          <div className="text-[10px] uppercase tracking-[0.4em] text-[#e8559a]/80 mb-3">
            {es ? "NUESTRA FILOSOFÍA" : "OUR PHILOSOPHY"}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { q_es: "¿QUÉ APRENDIÓ EL JUGADOR?", q_en: "WHAT DID THE PLAYER LEARN?" },
              { q_es: "¿EN QUÉ MEJORÓ?", q_en: "WHAT DID THE PLAYER IMPROVE?" },
              { q_es: "¿CUÁL ES EL SIGUIENTE OBJETIVO?", q_en: "WHAT IS THE NEXT GOAL?" },
            ].map((x, i) => (
              <div key={i}>
                <div className="font-bebas text-4xl text-[#e8559a] mb-2">0{i + 1}</div>
                <div className="font-bebas text-2xl tracking-wider text-white leading-tight">
                  {es ? x.q_es : x.q_en}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Programs */}
      <section className="relative max-w-6xl mx-auto px-4 sm:px-6 py-12">
        <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
          <h2 className="font-bebas text-4xl sm:text-5xl tracking-[0.05em]">
            {es ? "PROGRAMAS" : "PROGRAMS"}
          </h2>
          <Link to="/programs" className="text-sm text-[#e8559a] hover:underline">
            {es ? "Ver todos →" : "View all →"}
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {programs.map((p) => (
            <Link
              key={p.key}
              to="/programs"
              data-testid={`program-${p.key}`}
              className="group rounded-2xl bg-[#161616] border border-[#252525] hover:border-[#e8559a]/50 p-6 transition-all"
            >
              <div className="text-4xl mb-3">{p.icon}</div>
              <h3 className="font-bebas text-2xl tracking-wider text-white">
                {es ? p.title_es : p.title_en}
              </h3>
              <p className="text-sm text-neutral-400 mt-1.5 leading-relaxed">
                {es ? p.desc_es : p.desc_en}
              </p>
              <div className="mt-4 text-xs text-[#e8559a]/80 group-hover:text-[#e8559a]">
                {es ? "Ver detalles →" : "See details →"}
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Before / After — Public Demo of Player Development */}
      <PublicDevelopmentDemo es={es} />

      {/* CTA */}
      <section className="relative max-w-6xl mx-auto px-4 sm:px-6 py-16 text-center">
        <h2 className="font-bebas text-4xl sm:text-6xl tracking-[0.03em]">
          {es ? "ÚNETE A LA TROPA" : "JOIN THE TROOP"}
        </h2>
        <p className="mt-3 text-neutral-400 max-w-xl mx-auto">
          {es ? "Registra a tu jugador y comienza el desarrollo profesional." : "Register your player and start professional development."}
        </p>
        <div className="mt-6 flex flex-wrap gap-3 justify-center">
          <Link to="/register" className="px-6 py-3.5 rounded-xl bg-[#e8559a] hover:bg-[#c9407f] text-white font-bebas text-lg tracking-[0.2em] transition-colors shadow-lg shadow-[#e8559a]/30">
            {es ? "REGISTRAR AHORA" : "REGISTER NOW"} →
          </Link>
          <Link to="/programs" className="px-6 py-3.5 rounded-xl bg-[#161616] border border-[#e8559a]/40 hover:border-[#e8559a] text-white font-bebas text-lg tracking-[0.2em] transition-colors">
            {es ? "RESERVAR ENTRENAMIENTO" : "BOOK TRAINING"}
          </Link>
        </div>
      </section>

      <footer className="relative border-t border-[#1a1a1a] mt-12 py-8 text-center text-xs text-neutral-500">
        SOCCER TROOP · @soccer.troop · Denver, Colorado · Est. 2022 · Train · Focus · Score
      </footer>
    </div>
  );
}

// ============ Public Development Demo (Before/After + Sample Profile CTA) ============
function PublicDevelopmentDemo({ es }) {
  const [cfg, setCfg] = useState(null);

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/demo/config`).then((r) => {
      if (alive) setCfg(r.data);
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  if (!cfg) {
    return (
      <section className="relative max-w-6xl mx-auto px-4 sm:px-6 py-12">
        <div className="rounded-3xl bg-[#161616] border border-[#252525] h-64 animate-pulse" />
      </section>
    );
  }

  const headline = (es ? cfg.landing_headline_es : cfg.landing_headline_en) || "";
  const subtext = es ? cfg.landing_subtext_es : cfg.landing_subtext_en;
  const skill = es ? cfg.skill_es : cfg.skill_en;
  const feedback = es ? cfg.coach_feedback_es : cfg.coach_feedback_en;
  const nextGoal = es ? cfg.next_goal_es : cfg.next_goal_en;

  return (
    <section className="relative max-w-6xl mx-auto px-4 sm:px-6 py-14">
      <div className="rounded-3xl bg-gradient-to-br from-[#161616] to-[#1a0e16] border border-[#e8559a]/30 p-6 sm:p-10">
        <div className="text-[10px] uppercase tracking-[0.4em] text-[#e8559a]/80 mb-3">
          {es ? "EJEMPLO DE DESARROLLO DE JUGADOR" : "SAMPLE PLAYER DEVELOPMENT"}
        </div>
        <h2 className="font-bebas text-4xl sm:text-6xl tracking-[0.03em] leading-[0.95] max-w-3xl whitespace-pre-line">
          {headline}
        </h2>
        <p className="mt-4 text-neutral-300 max-w-2xl leading-relaxed">
          {subtext}
        </p>

        {/* Before / After Videos */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
          <DemoVideoCard
            label={es ? "ANTES" : "BEFORE"}
            week={cfg.before_date_label}
            videoId={cfg.before_video_id}
            accent="border-neutral-500/40 text-neutral-400"
            data-testid="demo-before-card"
          />
          <DemoVideoCard
            label={es ? "DESPUÉS" : "AFTER"}
            week={cfg.after_date_label}
            videoId={cfg.after_video_id}
            accent="border-[#e8559a]/50 text-[#e8559a]"
            data-testid="demo-after-card"
          />
        </div>

        {/* Meta info */}
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
          <MetaTag emoji="🎯" label={es ? "Habilidad" : "Skill"} value={skill} accent="text-[#e8559a] border-[#e8559a]/30 bg-[#e8559a]/5" />
          <MetaTag emoji="💬" label={es ? "Feedback del coach" : "Coach feedback"} value={feedback} accent="text-emerald-300 border-emerald-500/30 bg-emerald-500/5" />
          <MetaTag emoji="🚀" label={es ? "Próximo objetivo" : "Next goal"} value={nextGoal} accent="text-sky-300 border-sky-500/30 bg-sky-500/5" />
        </div>

        {/* Skill bars */}
        <div className="mt-8">
          <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-3">
            {es ? "INDICADORES DE COACHING · NO SON PUNTAJES CIENTÍFICOS" : "COACHING INDICATORS · NOT SCIENTIFIC SCORES"}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
            {(cfg.skill_bars || []).slice(0, 4).map((b, i) => (
              <SkillBar key={i} name={es ? b.name_es : b.name_en} value={b.value} />
            ))}
          </div>
        </div>

        {/* CTA to demo profile */}
        <div className="mt-8 flex flex-wrap gap-3">
          <Link
            to="/demo"
            data-testid="cta-view-demo-profile"
            className="px-6 py-3.5 rounded-xl bg-[#e8559a] hover:bg-[#c9407f] text-white font-bebas text-lg tracking-[0.2em] transition-colors shadow-lg shadow-[#e8559a]/30"
          >
            {es ? "VER PERFIL DE EJEMPLO" : "VIEW SAMPLE PLAYER PROFILE"} →
          </Link>
          <div className="text-xs text-neutral-500 self-center">
            {es ? "Sin registro. Sin login. Es un ejemplo real." : "No signup. No login. This is a real example."}
          </div>
        </div>
      </div>
    </section>
  );
}

export function DemoVideoCard({ label, week, videoId, accent, ...rest }) {
  const [playing, setPlaying] = useState(false);
  const src = videoId ? `${API}/demo/videos/${videoId}/file` : null;
  return (
    <div
      className={`relative rounded-2xl bg-black border-2 ${accent} overflow-hidden`}
      {...rest}
    >
      <div className="aspect-video w-full bg-[#050505] flex items-center justify-center">
        {src && playing ? (
          <video
            src={src}
            controls
            autoPlay
            playsInline
            className="w-full h-full object-contain bg-black"
          />
        ) : src ? (
          <button
            type="button"
            onClick={() => setPlaying(true)}
            className="absolute inset-0 flex flex-col items-center justify-center group"
          >
            <video
              src={src}
              muted
              preload="metadata"
              className="absolute inset-0 w-full h-full object-cover opacity-60 group-hover:opacity-80 transition-opacity"
            />
            <div className="relative z-10 w-16 h-16 rounded-full bg-[#e8559a] flex items-center justify-center shadow-lg shadow-[#e8559a]/40 group-hover:scale-110 transition-transform">
              <span className="text-white text-2xl ml-1">▶</span>
            </div>
          </button>
        ) : (
          <div className="text-center px-4">
            <div className="text-5xl opacity-30">▶</div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-600 mt-3">
              Coming soon
            </div>
          </div>
        )}
      </div>
      <div className="absolute top-3 left-3 flex items-center gap-2 z-20 pointer-events-none">
        <span className={`px-2.5 py-1 rounded-md text-[10px] uppercase tracking-[0.25em] font-bebas bg-black/70 border ${accent}`}>
          {label}
        </span>
      </div>
      {week && (
        <div className="absolute top-3 right-3 z-20 pointer-events-none">
          <span className="px-2.5 py-1 rounded-md text-[10px] uppercase tracking-widest font-medium bg-black/70 border border-white/20 text-white">
            {week}
          </span>
        </div>
      )}
    </div>
  );
}

function MetaTag({ emoji, label, value, accent }) {
  if (!value) return null;
  return (
    <div className={`rounded-xl border p-3.5 ${accent}`}>
      <div className="text-[10px] uppercase tracking-[0.2em] opacity-80 mb-1">
        {emoji} {label}
      </div>
      <div className="text-sm text-white leading-relaxed">{value}</div>
    </div>
  );
}

function SkillBar({ name, value }) {
  const v = Math.max(0, Math.min(100, value || 0));
  return (
    <div>
      <div className="flex items-center justify-between text-xs mb-1">
        <span className="uppercase tracking-[0.15em] text-neutral-300">{name}</span>
        <span className="text-[#e8559a] font-bebas tracking-widest">{v}</span>
      </div>
      <div className="h-1.5 bg-[#0a0a0a] border border-[#252525] rounded-full overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-[#e8559a] to-[#c9407f] rounded-full transition-all duration-1000"
          style={{ width: `${v}%` }}
        />
      </div>
    </div>
  );
}
